import axios from 'axios';
import { buildUrl } from 'Util/FetchUtils';
import { API_ENDPOINTS, BOOKING_ENTITY_URL } from 'Constants';
import { BookingListSummaryDto } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import { ParkingReservationDto } from 'Services/Api/_HumanWritten/ParkingService';

export interface Bookings {
	totalBookingCount: number;
	bookings: BookingListSummaryDto[];
	parkingReservations: ParkingReservationDto[];
	totalParkingCount: number;
}

const ferryBookingTransactionIdStorageKey = 'ferry-booking-transaction-id-key';
const eventBookingTransactionIdStorageKey = 'event-booking-transaction-id-key';

export function getFerryBookingTransactionIdFromStorage(): string | null {
	return sessionStorage?.getItem(ferryBookingTransactionIdStorageKey);
}

export function setFerryBookingTransactionIdInStorage(transactionId: string) {
	sessionStorage.setItem(ferryBookingTransactionIdStorageKey, transactionId);
}

export function clearFerryBookingTransactionIdFromStorage() {
	sessionStorage.removeItem(ferryBookingTransactionIdStorageKey);
}

export function getEventBookingTransactionIdFromStorage(): string | null {
	return sessionStorage?.getItem(eventBookingTransactionIdStorageKey);
}

export function setEventBookingTransactionIdInStorage(transactionId: string) {
	sessionStorage.setItem(eventBookingTransactionIdStorageKey, transactionId);
}

export function clearEventBookingTransactionIdFromStorage() {
	sessionStorage.removeItem(eventBookingTransactionIdStorageKey);
}

export function markBookingAsInvoiced(bookingId: string) {
	return axios.get(buildUrl(`${BOOKING_ENTITY_URL}/mark-booking-invoiced`, { bookingId }));
}

export function markBulkBookingsAsInvoiced(bookingIds: string[]) {
	return axios.post(`${BOOKING_ENTITY_URL}/mark-bulk-bookings-invoiced`, {
		bookingIds,
	});
}

export function undoBookingInvoiced(bookingId: string) {
	return axios.get(buildUrl(`${BOOKING_ENTITY_URL}/undo-booking-invoiced`, { bookingId }));
}

export function togglePaymentTaken(bookingId: string, paymentTaken: boolean) {
	return axios.get(
		buildUrl(`${BOOKING_ENTITY_URL}/toggle-payment-taken`, { bookingId, paymentTaken: paymentTaken.toString() }),
	);
}

export function processManualRefund(refundId: string, note?: string) {
	return axios.get(buildUrl(`${BOOKING_ENTITY_URL}/process-manual-refund`, { refundId, refundNote: note ?? '' }));
}

export function cancelManualRefund(refundId: string, note?: string) {
	return axios.get(buildUrl(`${BOOKING_ENTITY_URL}/cancel-manual-refund`, { refundId, refundNote: note ?? '' }));
}

export async function FetchBookingsForCustomerBookingsList(
	userId: string | null,
	upcoming: boolean,
	skip: number,
	bookingType: string,
): Promise<Bookings> {
	const results = await axios.get(
		buildUrl(`${BOOKING_ENTITY_URL}/fetch-filtered-bookings`, {
			userId: userId ?? '',
			upcoming: upcoming.toString(),
			skip: skip.toString(),
			bookingType: bookingType !== 'ALL' ? bookingType : '',
		}),
	);
	const parking = results.data.parking;
	return {
		totalBookingCount: results.data.count,
		bookings: results.data.bookings ?? [],
		parkingReservations: parking?.reservations ?? [],
		totalParkingCount: results.data.parkingCount ?? 0,
	};
}

export interface MyBookingKinds {
	hasFerryBookings: boolean;
	hasEventBookings: boolean;
	hasParkingReservations: boolean;
}

/**
 * Which kinds of booking the signed-in customer actually has. App config says which kinds the client
 * sells; this says which this customer has bought, and the bookings page needs both before it offers
 * a type chip — a chip whose only outcome is an empty list is not worth the room.
 */
export async function FetchMyBookingKinds(): Promise<MyBookingKinds> {
	const results = await axios.get(`${BOOKING_ENTITY_URL}/my-booking-kinds`);
	return {
		hasFerryBookings: results.data?.hasFerryBookings === true,
		hasEventBookings: results.data?.hasEventBookings === true,
		hasParkingReservations: results.data?.hasParkingReservations === true,
	};
}

export async function SplitBooking(bookingId: string, departureBooking: boolean): Promise<string> {
	const res = await axios.post(`${BOOKING_ENTITY_URL}/split-booking`, {
		bookingId,
		departureBooking,
	});
	return res.data;
}

interface ResendConfirmationEmailRequest {
	bookingIds: string[];
	email: string;
	transactionId: string | null;
}

export async function triggerSendConfirmationEmail(bookingIds: string[], email: string, transactionId?: string) {
	const request: ResendConfirmationEmailRequest = { bookingIds, email, transactionId: transactionId ?? null };
	await axios.post(API_ENDPOINTS.BOOKINGS.POST_RESEND_CONFIRMATION, request);
}
